import { HoverCard, HoverCardTrigger, HoverCardContent } from './chunk-5EZ6UUAR.js';
import { Popover, PopoverTrigger, PopoverContent } from './chunk-BLADNTOL.js';
import { Input } from './chunk-VYHSTPFS.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React3 from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown, Check, Pipette } from 'lucide-react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { readableForeground, normalizeHex, isHexColor, CATALOG_PALETTE } from '@authe/fe-commons/color';

function ViewModeToggle({
  options,
  value,
  onChange,
  size = "md",
  className,
  ...rest
}) {
  const buttons = React3.useRef(/* @__PURE__ */ new Map());
  const enabled = options.filter((option) => !option.disabled);
  const tabStop = enabled.some((option) => option.value === value) ? value : enabled[0]?.value;
  const pad = size === "sm" ? "h-7 px-2 text-xs" : "h-8 px-2.5 text-sm";
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "radiogroup",
      "aria-label": rest["aria-label"],
      className: cn("inline-flex items-center gap-1 rounded-lg bg-muted p-1 text-muted-foreground", className),
      children: options.map((option) => {
        const active = option.value === value;
        return /* @__PURE__ */ jsxs(
          "button",
          {
            ref: (element) => {
              if (element) buttons.current.set(option.value, element);
              else buttons.current.delete(option.value);
            },
            type: "button",
            tabIndex: option.value === tabStop ? 0 : -1,
            onKeyDown: (event) => {
              if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key) || !enabled.length) return;
              event.preventDefault();
              const current = enabled.findIndex((item) => item.value === option.value);
              const next = event.key === "Home" ? 0 : event.key === "End" ? enabled.length - 1 : (current + (event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1) + enabled.length) % enabled.length;
              const target = enabled[next];
              onChange(target.value);
              buttons.current.get(target.value)?.focus();
            },
            role: "radio",
            "aria-checked": active,
            disabled: option.disabled,
            title: option.title,
            onClick: () => !active && !option.disabled && onChange(option.value),
            className: cn(
              "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium ring-offset-background transition-all",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
              pad,
              active ? "bg-background text-foreground shadow" : "hover:text-foreground"
            ),
            children: [
              option.icon,
              option.label
            ]
          },
          option.value
        );
      })
    }
  );
}
function FilterChip({
  active,
  onClick,
  count,
  disabled = false,
  title,
  keepFocus = false,
  children,
  className
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      "aria-pressed": active,
      disabled,
      title,
      onMouseDown: keepFocus ? (e) => e.preventDefault() : void 0,
      onClick,
      className: cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        active ? "border-primary bg-primary/10 font-medium text-primary" : "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
        className
      ),
      children: [
        children,
        count != null && /* @__PURE__ */ jsx("span", { className: cn("tabular-nums", active ? "text-primary/70" : "text-muted-foreground/70"), children: count.toLocaleString() })
      ]
    }
  );
}
var NativeSelect = React3.forwardRef(
  function NativeSelect2({ className, density = "default", ...props }, ref) {
    return /* @__PURE__ */ jsx(
      "select",
      {
        ref,
        className: cn(
          "flex w-full rounded-md border border-input bg-background px-2 py-1 ring-offset-background",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          density === "sm" ? "h-8 text-xs" : "h-9 text-sm",
          className
        ),
        ...props
      }
    );
  }
);
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectLabel = React3.forwardRef(function SelectLabel2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    SelectPrimitive.Label,
    {
      ref,
      className: cn("px-2 py-1.5 text-xs font-medium text-muted-foreground", className),
      ...props
    }
  );
});
var SelectTrigger = React3.forwardRef(function SelectTrigger2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ jsxs(
    SelectPrimitive.Trigger,
    {
      ref,
      className: cn(
        // `[&>span]:min-w-0 truncate`, not `line-clamp-1`: a line clamp cannot
        // shorten a `whitespace-nowrap` label in a flex row, so a long value ran
        // under the chevron (the Team page's switcher, on any long team name).
        "flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:min-w-0 [&>span]:truncate",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 opacity-50" }) })
      ]
    }
  );
});
var SelectContent = React3.forwardRef(function SelectContent2({ className, children, position = "popper", ...props }, ref) {
  return /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsx(
    SelectPrimitive.Content,
    {
      ref,
      position,
      className: cn(
        // `min(...)` of the two caps, not the fixed 24rem alone: that cap is a
        // taste limit ("a select should not be half the screen") and says
        // nothing about whether there is 24rem of room. On a short window, or
        // a select opened near the bottom, the list overran the viewport and
        // `overflow-hidden` clipped the rest away. The available height is the
        // room Radix actually measured, so whichever is smaller wins.
        "relative z-50 max-h-[min(24rem,var(--radix-select-content-available-height))] min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out",
        position === "popper" && "data-[side=bottom]:translate-y-1",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(
        SelectPrimitive.Viewport,
        {
          className: cn("p-1", position === "popper" && "w-full min-w-[var(--radix-select-trigger-width)]"),
          children
        }
      )
    }
  ) });
});
var SelectItem = React3.forwardRef(function SelectItem2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ jsxs(
    SelectPrimitive.Item,
    {
      ref,
      className: cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx("span", { className: "absolute right-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" }) }) }),
        /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })
      ]
    }
  );
});
var EMPTY_SENTINEL = "__empty__";
function EnumSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  id,
  className,
  contentClassName,
  "aria-label": ariaLabel
}) {
  const offersEmpty = options.some((option) => option.value === "");
  const selectValue = value === "" ? offersEmpty ? EMPTY_SENTINEL : void 0 : value || void 0;
  const handleChange = (next) => onChange(next === EMPTY_SENTINEL ? "" : next);
  return /* @__PURE__ */ jsxs(Select, { value: selectValue, onValueChange: handleChange, disabled, children: [
    /* @__PURE__ */ jsx(SelectTrigger, { id, className, "aria-label": ariaLabel, children: /* @__PURE__ */ jsx(SelectValue, { placeholder }) }),
    /* @__PURE__ */ jsx(SelectContent, { className: contentClassName, children: options.map((option) => /* @__PURE__ */ jsx(
      SelectItem,
      {
        value: option.value === "" ? EMPTY_SENTINEL : option.value,
        disabled: option.disabled,
        children: option.label
      },
      option.value || EMPTY_SENTINEL
    )) })
  ] });
}
var Slider = React3.forwardRef(function Slider2({ className, ...props }, ref) {
  const thumbCount = Array.isArray(props.value) ? props.value.length : Array.isArray(props.defaultValue) ? props.defaultValue.length : 1;
  return /* @__PURE__ */ jsxs(
    SliderPrimitive.Root,
    {
      ref,
      className: cn("relative flex w-full touch-none select-none items-center", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx(SliderPrimitive.Track, { className: "relative h-1.5 w-full grow overflow-hidden rounded-full bg-secondary", children: /* @__PURE__ */ jsx(SliderPrimitive.Range, { className: "absolute h-full bg-brand" }) }),
        Array.from({ length: thumbCount }).map((_, i) => /* @__PURE__ */ jsx(
          SliderPrimitive.Thumb,
          {
            className: "block h-4 w-4 rounded-full border border-brand/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          },
          i
        ))
      ]
    }
  );
});
var Collapsible = CollapsiblePrimitive.Root;
var CollapsibleTrigger2 = CollapsiblePrimitive.CollapsibleTrigger;
var CollapsibleContent2 = CollapsiblePrimitive.CollapsibleContent;
var SWATCH_SIZE = {
  sm: "h-5 w-5",
  md: "h-7 w-7",
  lg: "h-9 w-9"
};
function ColorSwatch({
  color,
  selected,
  onSelect,
  size = "md",
  title,
  className
}) {
  const fg = readableForeground(color);
  const Comp = onSelect ? "button" : "span";
  return /* @__PURE__ */ jsx(
    Comp,
    {
      ...onSelect ? { type: "button", onClick: onSelect } : {},
      title: title ?? color,
      "aria-label": title ?? color,
      "aria-pressed": onSelect ? Boolean(selected) : void 0,
      className: cn(
        "inline-flex items-center justify-center rounded-md border border-black/10 shadow-sm transition-transform",
        onSelect && "cursor-pointer hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected && "ring-2 ring-ring ring-offset-1",
        SWATCH_SIZE[size],
        className
      ),
      style: { backgroundColor: color },
      children: selected ? /* @__PURE__ */ jsx(Check, { className: "h-3.5 w-3.5", style: { color: fg } }) : null
    }
  );
}
function ColorPicker({
  value,
  onChange,
  palette = CATALOG_PALETTE.map((s) => s.hex),
  allowCustom = true,
  allowClear = true,
  disabled,
  placeholder = "Pick a colour",
  id,
  className
}) {
  const [open, setOpen] = React3.useState(false);
  const [draft, setDraft] = React3.useState(value ?? "");
  React3.useEffect(() => setDraft(value ?? ""), [value]);
  const normalized = normalizeHex(value);
  const commitCustom = () => {
    const next = normalizeHex(draft);
    if (next) {
      onChange(next);
      setOpen(false);
    }
  };
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: disabled ? void 0 : setOpen, children: [
    /* @__PURE__ */ jsxs(
      PopoverTrigger,
      {
        id,
        type: "button",
        disabled,
        className: cn(
          "inline-flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50",
          className
        ),
        children: [
          normalized ? /* @__PURE__ */ jsx("span", { className: "inline-block h-4 w-4 rounded border border-black/10", style: { backgroundColor: normalized } }) : /* @__PURE__ */ jsx(Pipette, { className: "h-4 w-4 text-muted-foreground" }),
          /* @__PURE__ */ jsx("span", { className: cn(!normalized && "text-muted-foreground"), children: normalized ?? placeholder })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(PopoverContent, { align: "start", className: "w-60 space-y-3", children: [
      /* @__PURE__ */ jsx("div", { className: "grid grid-cols-6 gap-2", children: palette.map((color) => /* @__PURE__ */ jsx(
        ColorSwatch,
        {
          color,
          selected: normalizeHex(color) === normalized,
          onSelect: () => {
            onChange(normalizeHex(color));
            setOpen(false);
          }
        },
        color
      )) }),
      allowCustom && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(
          Input,
          {
            "aria-label": "Custom hex colour",
            value: draft,
            placeholder: "#rrggbb",
            onChange: (e) => setDraft(e.target.value),
            onKeyDown: (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitCustom();
              }
            },
            className: cn("h-8 font-mono text-xs", draft && !isHexColor(draft) && "border-destructive")
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: commitCustom,
            disabled: !isHexColor(draft),
            className: "shrink-0 rounded-md border px-2 py-1 text-xs hover:bg-accent disabled:opacity-50",
            children: "Set"
          }
        )
      ] }),
      allowClear && normalized && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => {
            onChange(null);
            setOpen(false);
          },
          className: "text-xs text-muted-foreground hover:text-foreground",
          children: "Clear colour"
        }
      )
    ] })
  ] });
}
function MiddleEllipsis({
  value,
  head = 6,
  tail = 6,
  className,
  title,
  monospace = true
}) {
  const base = cn("text-sm", monospace && "font-mono", className);
  if (value.length <= head + tail + 1) {
    return /* @__PURE__ */ jsx("span", { className: base, title: title ?? value, children: value });
  }
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap align-middle", base),
      style: { maxWidth: `${head + tail + 1}ch` },
      title: title ?? value,
      children: value
    }
  );
}
function RichTooltip({
  children,
  title,
  subtitle,
  headerRight,
  rows,
  actions,
  content,
  align = "start",
  side = "top",
  openDelay = 150,
  closeDelay = 80,
  className,
  asChild = true
}) {
  const hasBody = Boolean(title || subtitle || rows && rows.length || content || actions && actions.length);
  return /* @__PURE__ */ jsxs(HoverCard, { openDelay, closeDelay, children: [
    /* @__PURE__ */ jsx(HoverCardTrigger, { asChild, children: asChild ? children : /* @__PURE__ */ jsx("span", { children }) }),
    hasBody && /* @__PURE__ */ jsxs(
      HoverCardContent,
      {
        align,
        side,
        className: cn("w-72 space-y-2 p-3 text-sm", className),
        children: [
          (title || subtitle || headerRight) && /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
            /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
              title && /* @__PURE__ */ jsx("div", { className: "truncate font-medium leading-tight", children: title }),
              subtitle && /* @__PURE__ */ jsx("div", { className: "truncate text-xs text-muted-foreground", children: subtitle })
            ] }),
            headerRight && /* @__PURE__ */ jsx("div", { className: "shrink-0", children: headerRight })
          ] }),
          rows && rows.length > 0 && /* @__PURE__ */ jsx("dl", { className: "space-y-1", children: rows.map((row, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-baseline justify-between gap-3", children: [
            /* @__PURE__ */ jsx("dt", { className: "shrink-0 text-xs text-muted-foreground", children: row.label }),
            /* @__PURE__ */ jsx("dd", { className: "min-w-0 truncate text-right text-xs font-medium", children: row.value })
          ] }, i)) }),
          content,
          actions && actions.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1 border-t pt-2", children: actions.map((action) => {
            const Icon2 = action.icon;
            return /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                onClick: (e) => {
                  e.stopPropagation();
                  action.onClick();
                },
                "aria-label": action.label,
                title: action.label,
                className: "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
                children: [
                  Icon2 && /* @__PURE__ */ jsx(Icon2, { className: "h-3.5 w-3.5" }),
                  !action.iconOnly && /* @__PURE__ */ jsx("span", { children: action.label })
                ]
              },
              action.label
            );
          }) })
        ]
      }
    )
  ] });
}

export { Collapsible, CollapsibleContent2 as CollapsibleContent, CollapsibleTrigger2 as CollapsibleTrigger, ColorPicker, ColorSwatch, EnumSelect, FilterChip, MiddleEllipsis, NativeSelect, RichTooltip, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue, Slider, ViewModeToggle };
//# sourceMappingURL=chunk-KKYW54TJ.js.map
//# sourceMappingURL=chunk-KKYW54TJ.js.map